import {Channel as TypeChannel, Event} from "stream-chat";

// Stream's GET /unread returns at most the last 100 unread channels
export const UNREAD_CHANNELS_LIMIT = 100;

// Same id the backend gives the driver channel (SourceDriver.getExternalChannelId in nxtgen-services)
export const getDriverChannelCid = (tenantId: string, driverId: number): string =>
  `driver:${tenantId}_driver_${driverId}`;

// Channels this user has unread messages in; isComplete is false when Stream cut the list at its limit
export type UnreadChannels = {
  cids: Set<string>;
  isComplete: boolean;
};

export const toUnreadChannels = (channels: {channel_id: string; unread_count: number}[]): UnreadChannels => ({
  cids: new Set(channels.filter((channel) => channel.unread_count > 0).map((channel) => channel.channel_id)),
  isComplete: channels.length < UNREAD_CHANNELS_LIMIT
});

// Keeps the unread list in step with real-time events between two GET /unread calls
export const applyUnreadEvent = (
  unreadChannels: UnreadChannels | undefined,
  event: Event,
  currentUserId: string | undefined
): UnreadChannels | undefined => {
  if (!unreadChannels) {
    return unreadChannels;
  }
  if (event.type === "notification.mark_read" && event.unread_channels === 0) {
    return {...unreadChannels, cids: new Set()};
  }
  if (!event.cid) {
    return unreadChannels;
  }
  const isRead =
    event.type === "notification.mark_read" || (event.type === "message.read" && event.user?.id === currentUserId);
  if (isRead) {
    const cids = new Set(unreadChannels.cids);
    cids.delete(event.cid);
    return {...unreadChannels, cids};
  }
  const author = event.user ?? event.message?.user;
  const isNewFromOthers =
    (event.type === "message.new" || event.type === "notification.message_new") && author?.id !== currentUserId;
  if (isNewFromOthers) {
    return {...unreadChannels, cids: new Set(unreadChannels.cids).add(event.cid)};
  }
  return unreadChannels;
};

export type SelectedDriverChannelResolution = {
  channel: TypeChannel | undefined;
  hasUnread: boolean;
  needsLookup: boolean;
};

export type ResolveSelectedDriverChannelParams = {
  activeChannels: Record<string, TypeChannel>;
  cid: string;
  dispatchGroupIds: (number | null | undefined)[];
  unreadChannels: UnreadChannels | undefined;
};

// Answers the selected driver's unread badge from what this tab already holds. A channel that is not loaded
// only needs a Stream query when it may be unread: then the query applies the dispatch-group filter as before.
export const resolveSelectedDriverChannel = ({
  activeChannels,
  cid,
  dispatchGroupIds,
  unreadChannels
}: ResolveSelectedDriverChannelParams): SelectedDriverChannelResolution => {
  const loaded = activeChannels[cid];
  const isInScope =
    !!loaded?.initialized &&
    (dispatchGroupIds.length === 0 || dispatchGroupIds.includes(loaded.data?.dispatch_group_id as number));
  if (isInScope) {
    return {channel: loaded, hasUnread: loaded.state.unreadCount > 0, needsLookup: false};
  }
  return {
    channel: undefined,
    hasUnread: false,
    needsLookup: !unreadChannels || !unreadChannels.isComplete || unreadChannels.cids.has(cid)
  };
};

// Selected driver's badge (0/1) implied by a client event, or undefined when the event does not affect it
export const getSelectedDriverUnreadFromEvent = (
  event: Event,
  selectedDriverChannelId: string | undefined
): 0 | 1 | undefined => {
  if (!selectedDriverChannelId || event.cid !== selectedDriverChannelId) {
    return undefined;
  }
  if (event.type === "notification.mark_read") {
    return 0;
  }
  const author = event.user ?? event.message?.user;
  if ((event.type === "message.new" || event.type === "notification.message_new") && author?.role === "driver") {
    return 1;
  }
  return undefined;
};
